import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@ui/alert';
import { Button } from '@ui/button';
import { RefreshCcw, AlertTriangle, Bug } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches JavaScript errors in its child component tree,
 * displays a fallback UI, and provides options to retry or report the error.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({
      errorInfo,
    });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render the custom fallback UI provided as a prop
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-[300px] flex flex-col items-center justify-center p-6 bg-muted rounded-xl">
          <div className="w-full max-w-md">
            <Alert variant="destructive" className="mb-6 border-2 border-destructive/20">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <AlertTitle className="text-xl font-bold mb-2">Something went wrong</AlertTitle>
              <AlertDescription className="mt-2">
                <p className="text-foreground mb-4">
                  We encountered an unexpected error. Please try again or contact support if the
                  issue persists.
                </p>
                {this.state.error && (
                  <div className="bg-destructive/10 p-3 rounded-md mb-4 border border-destructive/20">
                    <p className="text-sm font-mono text-destructive break-words">
                      {this.state.error.toString()}
                    </p>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button onClick={this.handleRetry} variant="default">
                    <RefreshCcw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={() => (window.location.href = '/')}>
                    Return to Home
                  </Button>
                </div>
              </AlertDescription>
            </Alert>

            {/* Optional error details that can be expanded (for development) */}
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <div className="mt-6 bg-card p-4 rounded-xl shadow-md">
                <details className="text-sm">
                  <summary className="cursor-pointer text-primary font-medium flex items-center">
                    <Bug className="h-4 w-4 mr-2" />
                    View Developer Details
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-xs bg-muted p-3 rounded border border-border max-h-[300px] overflow-auto">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
